import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  fetchMe,
  logout as postLogout,
  refreshSession,
  type MeResponse,
  type RefreshSessionResult,
} from '@/api/me'
import { SSOAttemptKey, LoginRedirectKey } from '@/router/guard'

type LoadState = 'idle' | 'loading' | 'loaded' | 'unavailable'

/**
 * auth store: owns the signed-in user bootstrap + router-guard plumbing.
 *
 * Deliberately narrow — domain data (orgs, teams, …) lives in @tanstack/vue-query
 * caches, not here. Pinia only holds what is truly app-global: who is logged
 * in, and helpers the router needs in synchronous guard contexts.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<MeResponse['user'] | null>(null)
  const memberships = ref<MeResponse['memberships']>([])
  const sessionExpiry = ref<string | null>(null)
  const canAccessAdmin = ref(false)
  const state = ref<LoadState>('idle')
  let inflight: Promise<void> | null = null
  let loadRetryTimer: ReturnType<typeof setTimeout> | null = null
  let loadRetryUsed = false
  const loadRetryIntervalMs = 5_000

  const isAuthenticated = computed(() => user.value !== null)
  const isUnavailable = computed(() => state.value === 'unavailable')
  const isLoaded = computed(() => state.value === 'loaded')

  function apply(me: MeResponse | null) {
    if (me && me.user) {
      user.value = me.user
      memberships.value = me.memberships
      sessionExpiry.value = me.session_expiry ?? null
      canAccessAdmin.value = me.can_access_admin === true
    } else {
      user.value = null
      memberships.value = []
      sessionExpiry.value = null
      canAccessAdmin.value = false
    }
  }

  function cancelLoadRetry(): void {
    if (!loadRetryTimer) return
    clearTimeout(loadRetryTimer)
    loadRetryTimer = null
  }

  function scheduleLoadRetry(): void {
    if (loadRetryTimer) return
    loadRetryTimer = setTimeout(() => {
      loadRetryTimer = null
      state.value = 'idle'
      void ensureLoaded()
    }, loadRetryIntervalMs)
  }

  async function ensureLoaded(): Promise<void> {
    if (state.value === 'loaded' || state.value === 'unavailable') return
    if (inflight) return inflight
    state.value = 'loading'
    inflight = (async () => {
      try {
        const me = await fetchMe()
        apply(me)
        scheduleRefresh()
        loadRetryUsed = false
        state.value = 'loaded'
      } catch {
        state.value = 'unavailable'
        if (!loadRetryUsed) {
          loadRetryUsed = true
          scheduleLoadRetry()
        }
      } finally {
        inflight = null
      }
    })()
    return inflight
  }

  // Auto-refresh: schedule a timer well under the access JWT TTL so the
  // browser walks straight from one valid wippy_at to the next without
  // the user ever seeing a logged-out moment. session_expiry tracks the
  // refresh-token lifetime (~30d) — we cap our cadence at a few minutes
  // so the access cookie (5m default) gets rotated long before it
  // expires. Beyond that, setTimeout silently saturates delays larger
  // than 2^31-1 ms (~24.8d) and fires immediately, which would spin a
  // refresh loop on a fresh login; the cap keeps us in safe territory.
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let fastRefreshRetryUsed = false
  const refreshMarginMs = 60_000
  const refreshIntervalMs = 4 * 60_000
  const refreshRetryIntervalMs = 5_000
  const minimumRefreshDelayMs = 1_000

  function cancelRefreshTimer(): void {
    if (!refreshTimer) return
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  function clearRefreshSchedule(): void {
    fastRefreshRetryUsed = false
    cancelRefreshTimer()
  }

  function millisecondsUntilSessionExpiry(): number | null {
    if (!sessionExpiry.value) return null
    const exp = Date.parse(sessionExpiry.value)
    return Number.isNaN(exp) ? null : exp - Date.now()
  }

  function expireSession(): void {
    clearRefreshSchedule()
    apply(null)
  }

  function scheduleSessionExpiry(): void {
    cancelRefreshTimer()
    const delay = millisecondsUntilSessionExpiry()
    if (delay === null) return
    if (delay <= 0) {
      expireSession()
      return
    }
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      expireSession()
    }, delay)
  }

  function scheduleRefresh(maxDelayMs = refreshIntervalMs, respectMargin = true): void {
    cancelRefreshTimer()
    const untilSessionExpiry = millisecondsUntilSessionExpiry()
    if (untilSessionExpiry !== null && untilSessionExpiry <= 0) {
      expireSession()
      return
    }
    const capMs = untilSessionExpiry ?? maxDelayMs
    const targetDelay =
      respectMargin && untilSessionExpiry !== null ? untilSessionExpiry - refreshMarginMs : maxDelayMs
    const delay = Math.min(maxDelayMs, Math.max(minimumRefreshDelayMs, targetDelay), capMs)
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      void refresh()
    }, delay)
  }

  function scheduleRefreshAfterOutage(): void {
    if (!fastRefreshRetryUsed) {
      fastRefreshRetryUsed = true
      scheduleRefresh(refreshRetryIntervalMs, false)
      return
    }
    const untilSessionExpiry = millisecondsUntilSessionExpiry()
    if (untilSessionExpiry !== null && untilSessionExpiry <= refreshMarginMs) {
      scheduleSessionExpiry()
      return
    }
    scheduleRefresh()
  }

  function scheduleAfterSuccessfulRefresh(): void {
    const untilSessionExpiry = millisecondsUntilSessionExpiry()
    if (untilSessionExpiry !== null && untilSessionExpiry <= refreshMarginMs) {
      scheduleSessionExpiry()
      return
    }
    scheduleRefresh()
  }

  async function reload(): Promise<void> {
    apply(await fetchMe(true))
    state.value = 'loaded'
  }

  async function refresh(): Promise<boolean> {
    // Cross-tab serialization. Without this, every open portal tab fires
    // its scheduleRefresh() timer at roughly the same instant (4-min
    // interval, set together at login), each POST /auth/refresh races
    // the others on the same wippy_rt cookie value, and the slowest one
    // hits refresh-token reuse detection — service_auth.go:Refresh
    // revokes the entire family, ClearAll fires, every tab silently
    // logs out. Web Locks is well-supported (Chrome 69+, Safari 15.4+,
    // Firefox 96+); the fallback path below preserves legacy-browser
    // behavior.
    const inner = async (): Promise<boolean> => {
      let result: RefreshSessionResult
      try {
        result = await refreshSession()
      } catch {
        result = 'unavailable'
      }
      if (result === 'unavailable') {
        scheduleRefreshAfterOutage()
        return false
      }
      if (result === 'invalid') {
        expireSession()
        return false
      }
      fastRefreshRetryUsed = false
      try {
        apply(await fetchMe())
      } catch {
        return user.value !== null
      } finally {
        scheduleAfterSuccessfulRefresh()
      }
      return user.value !== null
    }
    if (typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function') {
      return navigator.locks.request('wippy_auth_refresh', { mode: 'exclusive' }, inner)
    }

    return inner()
  }

  async function logout(): Promise<void> {
    await postLogout()
    apply(null)
    cancelLoadRetry()
    loadRetryUsed = false
    clearRefreshSchedule()
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(SSOAttemptKey)
      sessionStorage.removeItem(LoginRedirectKey)
    }
  }

  function reset(): void {
    apply(null)
    cancelLoadRetry()
    loadRetryUsed = false
    clearRefreshSchedule()
    state.value = 'idle'
  }

  return {
    user,
    memberships,
    sessionExpiry,
    canAccessAdmin,
    isAuthenticated,
    isUnavailable,
    isLoaded,
    ensureLoaded,
    reload,
    refresh,
    logout,
    reset,
  }
})
